import { useRef, useCallback, useEffect, useState } from 'react';
import { CanvasArea } from '../components/CanvasArea';
import { EditorToolbar } from '../components/EditorToolbar';
import { ToolPalette } from '../components/ToolPalette';
import { PropertiesPanel } from '../components/PropertiesPanel';
import { StatusBar } from '../components/StatusBar';
import { ExportDialog } from '../components/ExportDialog';
import { useImageEditorState } from '../hooks/useImageEditorState';
import { useFabricCanvas } from '../hooks/useFabricCanvas';
import { useCanvasHistory } from '../hooks/useCanvasHistory';
import { useImageExport } from '../hooks/useImageExport';
import type { EditorTool, ExportSettings } from '../types';

export function EditorPage() {
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasImage, setHasImage] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);

  const {
    state,
    setActiveTool,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateFilterSettings,
    setZoom,
    setCanvasDimensions,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    setCursorInfo,
  } = useImageEditorState();

  const history = useCanvasHistory({ maxHistory: 50 });

  const canvas = useFabricCanvas({
    canvasElRef,
    wrapperRef,
    onObjectModified: () => {
      markDirty();
      history.saveSnapshot();
    },
    onSelectionChanged: setSelection,
    onCanvasDimensions: setCanvasDimensions,
    onCursorMove: setCursorInfo,
    onPathCreated: () => {
      markDirty();
      history.saveSnapshot();
    },
    onZoomChanged: setZoom,
  });

  // Connect canvas to history
  useEffect(() => {
    if (canvas.canvasRef.current) {
      history.setCanvas(
        canvas.canvasRef.current as unknown as Parameters<typeof history.setCanvas>[0],
      );
    }
  }, [canvas.canvasRef, history]);

  const { isExporting, exportToLocal, exportToFias } = useImageExport({
    exportToDataURL: canvas.exportToDataURL,
    exportToBlob: canvas.exportToBlob,
  });

  // Handle file loading
  const loadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        const fitZoom = await canvas.loadImage(dataUrl);
        setZoom(fitZoom);
        setFileInfo(file.name.replace(/\.[^.]+$/, ''), null);
        setHasImage(true);
        markClean();
        history.clearHistory();
        history.saveSnapshot();
      };
      reader.readAsDataURL(file);
    },
    [canvas, setFileInfo, markClean, history],
  );

  // Tool switching
  const handleToolSelect = useCallback(
    (tool: EditorTool) => {
      setActiveTool(tool);
      canvas.setTool(tool, state.brushSettings);

      // Auto-add text or shape on tool select
      if (tool === 'text') {
        canvas.addText('Double-click to edit', {
          fontSize: state.textSettings.fontSize,
          fill: state.textSettings.fill,
          fontFamily: state.textSettings.fontFamily,
        });
      }
      if (tool === 'shape') {
        canvas.addShape(state.shapeSettings.type, state.shapeSettings);
      }
    },
    [setActiveTool, canvas, state.brushSettings, state.textSettings, state.shapeSettings],
  );

  // Apply filters when settings change
  useEffect(() => {
    canvas.applyFilters(state.filterSettings);
  }, [state.filterSettings, canvas]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        history.undoCanvas();
      } else if (ctrl && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        history.redoCanvas();
      } else if (ctrl && e.key === 'y') {
        e.preventDefault();
        history.redoCanvas();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        canvas.deleteSelected();
      } else if (ctrl && e.key === 'a') {
        e.preventDefault();
        canvas.selectAll();
      } else if (ctrl && e.key === 's') {
        e.preventDefault();
        setExportDialogOpen(true);
      }

      // Tool shortcuts
      if (!ctrl && !e.shiftKey) {
        const toolMap: Record<string, EditorTool> = {
          v: 'select',
          c: 'crop',
          b: 'draw',
          t: 'text',
          u: 'shape',
          e: 'eraser',
          g: 'fill',
          i: 'eyedropper',
        };
        const tool = toolMap[e.key.toLowerCase()];
        if (tool) handleToolSelect(tool);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvas, history, handleToolSelect]);

  const handleExportLocal = useCallback(
    (fileName: string, format: 'png' | 'jpeg' | 'webp', quality: number) => {
      exportToLocal(fileName, format, quality);
    },
    [exportToLocal],
  );

  const handleExportFias = useCallback(
    async (settings: ExportSettings, fileName: string) => {
      const dataUrl = canvas.exportToDataURL(settings.format, settings.quality / 100);
      await exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: {
          format: settings.format,
          quality: settings.quality,
          width: settings.width,
          height: settings.height,
          maintainAspectRatio: settings.maintainAspectRatio,
        },
        saveAs: { fileName },
      });
    },
    [canvas, exportToFias],
  );

  const handleOpenFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="flex flex-col h-screen bg-background">
      <EditorToolbar
        fileName={state.fileName}
        isDirty={state.isDirty}
        zoom={state.zoom}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        onUndo={() => history.undoCanvas()}
        onRedo={() => history.redoCanvas()}
        onZoomIn={() => {
          const newZoom = Math.min(state.zoom * 1.25, 10);
          setZoom(newZoom);
          canvas.setZoom(newZoom);
        }}
        onZoomOut={() => {
          const newZoom = Math.max(state.zoom / 1.25, 0.1);
          setZoom(newZoom);
          canvas.setZoom(newZoom);
        }}
        onFitToScreen={() => {
          const fitZoom = canvas.fitToContainer();
          setZoom(fitZoom ?? 1);
        }}
        onRotateLeft={() => canvas.rotateCanvas(-90)}
        onRotateRight={() => canvas.rotateCanvas(90)}
        onFlipH={() => canvas.flipCanvas('horizontal')}
        onFlipV={() => canvas.flipCanvas('vertical')}
        onSave={() => setExportDialogOpen(true)}
        onOpenFile={handleOpenFile}
        gridVisible={gridVisible}
        onToggleGrid={() => setGridVisible((v) => !v)}
      />

      <div className="flex flex-1 overflow-hidden">
        <ToolPalette activeTool={state.activeTool} onToolSelect={handleToolSelect} />

        <CanvasArea
          canvasElRef={canvasElRef}
          wrapperRef={wrapperRef}
          onFileDropped={loadFile}
          onFileSelected={loadFile}
          hasImage={hasImage}
          gridVisible={gridVisible}
          viewport={canvas.viewport}
        />

        <PropertiesPanel
          isOpen={state.propertiesPanelOpen}
          activeTool={state.activeTool}
          brushSettings={state.brushSettings}
          textSettings={state.textSettings}
          shapeSettings={state.shapeSettings}
          filterSettings={state.filterSettings}
          onUpdateBrush={updateBrushSettings}
          onUpdateText={updateTextSettings}
          onUpdateShape={updateShapeSettings}
          onUpdateFilter={updateFilterSettings}
          onToggle={togglePropertiesPanel}
        />
      </div>

      <StatusBar
        zoom={state.zoom}
        canvasWidth={state.canvasWidth}
        canvasHeight={state.canvasHeight}
        cursorX={state.cursorPosition.x}
        cursorY={state.cursorPosition.y}
        colorUnderCursor={state.colorUnderCursor}
      />

      <ExportDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExportLocal={handleExportLocal}
        onExportFias={handleExportFias}
        defaultFileName={state.fileName}
        canvasWidth={state.canvasWidth}
        canvasHeight={state.canvasHeight}
      />

      {/* Hidden file input for toolbar open */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) loadFile(file);
          e.target.value = '';
        }}
      />

      {/* Loading overlay */}
      {isExporting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-background rounded-lg p-6 shadow-xl">
            <p className="text-sm">Exporting...</p>
          </div>
        </div>
      )}
    </div>
  );
}
